import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { PipelineStage, PreflightCheck, PromoteResponse } from '../api'
import { useEscapeKey } from '@marque/ui'

function tagsMatch(a: PipelineStage, b: PipelineStage): boolean {
    return Object.keys(a.tags).every(k => a.tags[k] === b.tags[k])
}

function tagToServiceDir(imageName: string): string {
    if (imageName === 'ops-api') return 'ops-api'
    return imageName.replace(/-api$/, '').replace(/-migrations$/, '')
}

interface PromoteArrowProps {
    source: PipelineStage
    target: PipelineStage
    onPromoting: (promoting: boolean) => void
}

export function PromoteArrow({ source, target, onPromoting }: PromoteArrowProps) {
    const queryClient = useQueryClient()
    const [showConfirm, setShowConfirm] = useState(false)
    // AB#2091 — keep the whole response so the badge can reflect verification state.
    const [result, setResult] = useState<PromoteResponse | null>(null)

    const synced = tagsMatch(source, target)

    const promote = useMutation({
        mutationFn: ({ services, force }: { services: string[] | undefined; force: boolean }) =>
            releaseApi.promote({
                sourceStage: source.name,
                targetStage: target.name,
                ...(services !== undefined && { services }),
                ...(force && { force }),
            }),
        onMutate: () => onPromoting(true),
        onSettled: () => onPromoting(false),
        onSuccess: (data) => {
            setResult(data)
            setShowConfirm(false)
            void queryClient.invalidateQueries({ queryKey: ['pipeline'] })
            void queryClient.invalidateQueries({ queryKey: ['pipeline-drift'] })
            void queryClient.invalidateQueries({ queryKey: ['promotion-history'] })
        },
    })

    const serviceOptions = [...new Set(Object.keys(source.tags).map(tagToServiceDir))].sort()

    return (
        <div className="flex flex-col items-center justify-center gap-1 shrink-0 w-16">
            {/* Connector line with arrow */}
            <div className="relative flex items-center w-full">
                <div
                    className={[
                        'h-0.5 w-full transition-colors duration-300',
                        synced ? 'bg-emerald-300' : 'bg-amber-300',
                    ].join(' ')}
                />
                <svg
                    className={[
                        'absolute right-0 -mr-1 h-3 w-3 transition-colors duration-300',
                        synced ? 'text-emerald-400' : 'text-amber-400',
                    ].join(' ')}
                    viewBox="0 0 12 12"
                    fill="currentColor"
                >
                    <path d="M0 5h10l-4-4 1.5-1.5L12 6l-4.5 6.5L6 11l4-4H0z" />
                </svg>
            </div>

            {/* Promote button (only when out of sync and not yet promoted this session) */}
            {!synced && !result && (
                <button
                    onClick={() => setShowConfirm(true)}
                    disabled={promote.isPending}
                    className="mt-1 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                >
                    {promote.isPending ? '…' : 'Promote'}
                </button>
            )}

            {/* Promoted badge — AB#2091: distinguishes verified from unverified rollouts */}
            {result && (
                result.verified ? (
                    <span
                        title={result.verificationDetail ?? undefined}
                        className="mt-1 rounded-full bg-emerald-900/40 px-2 py-0.5 text-[10px] font-semibold text-emerald-400 ring-1 ring-emerald-700/50 whitespace-nowrap"
                    >
                        Promoted ✓ verified
                    </span>
                ) : (
                    <span
                        title={result.verificationDetail ?? 'Verification did not complete — check ArgoCD.'}
                        className="mt-1 rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] font-semibold text-amber-400 ring-1 ring-amber-700/50 whitespace-nowrap"
                    >
                        Promoted — unverified
                    </span>
                )
            )}

            {/* AB#2091 — surfaced (previously swallowed) force-sync failures */}
            {result?.syncErrors && result.syncErrors.length > 0 && (
                <span
                    title={result.syncErrors.join('\n')}
                    className="mt-0.5 text-[10px] text-amber-400 text-center whitespace-nowrap"
                >
                    sync trigger failed
                </span>
            )}

            {/* Error */}
            {promote.isError && (
                <span className="mt-1 text-[10px] text-red-600 text-center">
                    {promote.error instanceof Error ? promote.error.message : 'Failed'}
                </span>
            )}

            {/* Confirmation dialog */}
            {showConfirm && (
                <ConfirmModal
                    source={source.name}
                    target={target.name}
                    sourceName={source.displayName}
                    targetName={target.displayName}
                    sourceTags={source.tags}
                    targetTags={target.tags}
                    serviceOptions={serviceOptions}
                    isPending={promote.isPending}
                    onConfirm={(services, force) => promote.mutate({ services, force })}
                    onCancel={() => setShowConfirm(false)}
                />
            )}
        </div>
    )
}

interface ConfirmModalProps {
    source: string
    target: string
    sourceName: string
    targetName: string
    sourceTags: Record<string, string>
    targetTags: Record<string, string>
    serviceOptions: string[]
    isPending: boolean
    onConfirm: (services: string[] | undefined, force: boolean) => void
    onCancel: () => void
}

function CheckRow({ check }: { check: PreflightCheck }) {
    return (
        <div className="flex items-start gap-2 text-sm">
            <span className={check.passed ? 'text-emerald-400 mt-0.5' : 'text-red-400 mt-0.5'}>
                {check.passed ? '✓' : '✗'}
            </span>
            <div>
                <span className={check.passed ? 'text-slate-200' : 'text-red-300'}>{check.name}</span>
                {!check.passed && check.overridable && (
                    <span className="ml-1.5 rounded bg-amber-500/15 px-1 py-px text-[10px] font-medium text-amber-400 ring-1 ring-amber-500/30">
                        overridable
                    </span>
                )}
                <p className="text-xs text-slate-400 mt-0.5">{check.detail}</p>
            </div>
        </div>
    )
}

function serviceHasDiff(service: string, sourceTags: Record<string, string>, targetTags: Record<string, string>): boolean {
    return Object.entries(sourceTags).some(([img, tag]) => {
        const dir = img === 'ops-api' ? 'ops-api' : img.replace(/-api$/, '').replace(/-migrations$/, '')
        return dir === service && tag !== targetTags[img]
    })
}

function ConfirmModal({
    source, target, sourceName, targetName,
    sourceTags, targetTags, serviceOptions,
    isPending, onConfirm, onCancel,
}: ConfirmModalProps) {
    useEscapeKey(onCancel)
    const { data: preflight, isLoading } = useQuery({
        queryKey: ['preflight', source, target],
        queryFn: () => releaseApi.preflight(source, target),
        staleTime: 30_000,
    })

    const [selected, setSelected] = useState<Set<string>>(new Set(serviceOptions))
    // AB#2091 — explicit force override for overridable (health-unknown) failures.
    const [force, setForce] = useState(false)

    function toggleService(service: string) {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(service)) {
                next.delete(service)
            } else {
                next.add(service)
            }
            return next
        })
    }

    const failedChecks = preflight?.checks.filter(c => !c.passed) ?? []
    // Force only helps when every failing check is overridable — a genuinely unhealthy
    // source blocks regardless, matching the server-side rule.
    const forceCanUnblock = failedChecks.length > 0 && failedChecks.every(c => c.overridable)
    const checksSatisfied = !isLoading && ((preflight?.canPromote ?? false) || (forceCanUnblock && force))
    const canPromote = checksSatisfied && selected.size > 0

    function handleConfirm() {
        const isAll = selected.size === serviceOptions.length
        onConfirm(isAll ? undefined : [...selected].sort(), force)
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-6 w-96 flex flex-col gap-4">
                <div>
                    <h2 className="text-base font-semibold text-white">
                        Promote {sourceName} → {targetName}
                    </h2>
                    <p className="mt-1 text-sm text-slate-400">
                        Copies selected image tags from{' '}
                        <strong className="text-slate-200">{sourceName}</strong> into{' '}
                        <strong className="text-slate-200">{targetName}</strong>, triggers an ArgoCD
                        sync, and verifies the rollout for up to 90 seconds.
                    </p>
                </div>

                {/* Service selection */}
                <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 flex flex-col gap-2">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Services</p>
                    {serviceOptions.map(svc => {
                        const hasDiff = serviceHasDiff(svc, sourceTags, targetTags)
                        return (
                            <label key={svc} className="flex items-center gap-2.5 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={selected.has(svc)}
                                    onChange={() => toggleService(svc)}
                                    className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-700 text-indigo-500 focus:ring-indigo-500/30 cursor-pointer"
                                />
                                <span className="text-sm font-mono text-slate-200 group-hover:text-white transition-colors">
                                    {svc}
                                </span>
                                {hasDiff
                                    ? <span className="ml-auto text-[10px] text-amber-400 font-medium">differs</span>
                                    : <span className="ml-auto text-[10px] text-slate-500">in sync</span>
                                }
                            </label>
                        )
                    })}
                </div>

                {/* Preflight checklist */}
                <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 flex flex-col gap-2">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Pre-flight checks</p>
                    {isLoading && (
                        <p className="text-sm text-slate-400 animate-pulse">Running checks…</p>
                    )}
                    {preflight?.checks.map(c => (
                        <CheckRow key={c.name} check={c} />
                    ))}

                    {/* AB#2091 — force override, shown only when it can actually unblock */}
                    {forceCanUnblock && (
                        <label className="mt-1 flex items-start gap-2.5 cursor-pointer rounded-lg border border-amber-700/40 bg-amber-950/30 p-2">
                            <input
                                type="checkbox"
                                checked={force}
                                onChange={() => setForce(f => !f)}
                                className="mt-0.5 w-3.5 h-3.5 rounded border-amber-600 bg-slate-700 text-amber-500 focus:ring-amber-500/30 cursor-pointer"
                            />
                            <span className="text-xs text-amber-300">
                                Force promotion — the source health is unknown, not known-good.
                                I accept promoting without a health signal.
                            </span>
                        </label>
                    )}
                </div>

                <div className="flex gap-2 justify-end">
                    <button
                        onClick={onCancel}
                        disabled={isPending}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isPending || !canPromote}
                        title={
                            selected.size === 0 ? 'Select at least one service' :
                            canPromote ? undefined : 'Resolve failing checks before promoting'
                        }
                        className={[
                            'rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                            force ? 'bg-amber-600 hover:bg-amber-500' : 'bg-blue-600 hover:bg-blue-500',
                        ].join(' ')}
                    >
                        {isPending ? 'Promoting…' : force ? 'Force promote' : 'Promote'}
                    </button>
                </div>
            </div>
        </div>
    )
}
